import React, { useState, useEffect, useRef } from 'react';
import { Modal }    from '../../../components/common/Modal';
import { Button }   from '../../../components/common/Button';
import { contactsAPI, projectsAPI } from '../../../services/api';
import { PlusIcon, TrashIcon, PhotoIcon, XMarkIcon, CloudArrowUpIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { CustomFieldsManager } from '../../../components/accounting/CustomFieldsManager';
import { customFieldsAPI } from '../../../services/api';
import { Cog6ToothIcon } from '@heroicons/react/24/outline';

const EMPTY_LINE = { description:'', quantity:1, unitPrice:0, taxRate:0, taxAmount:0, total:0 };

const PAYMENT_MODES = ['cash','bank_transfer','cheque','upi','card','other'];

// ── Per-type config ───────────────────────────────────────────
const TYPE_CONFIG = {
  sale: {
    title: 'Tax Invoice',
    statusOptions: ['draft','pending','approved','paid','cancelled','overdue'],
    showDueDate: true, showDeliveryDate: false, showShipping: true,
    showTransport: false, showCategory: false, showProject: false,
    partyLabel: 'Bill To (Customer)', partyPlaceholder: 'Search customer...',
    lineLabel: 'Products / Services',
    showPayment: true, showPaidAmount: true,
    showTerms: true, showShippingAddr: true,
    extraFields: ['poNumber'],
  },
  purchase: {
    title: 'Purchase Order',
    statusOptions: ['draft','pending','approved','paid','cancelled'],
    showDueDate: true, showDeliveryDate: true, showShipping: false,
    showTransport: false, showCategory: false, showProject: true,
    partyLabel: 'Vendor / Supplier', partyPlaceholder: 'Search vendor...',
    lineLabel: 'Items Ordered',
    showPayment: true, showPaidAmount: true,
    showTerms: true, showShippingAddr: false,
    extraFields: ['poNumber'],
  },
  quotation: {
    title: 'Quotation',
    statusOptions: ['draft','pending','approved','cancelled'],
    showDueDate: true, showDeliveryDate: false, showShipping: false,
    showTransport: false, showCategory: false, showProject: true,
    partyLabel: 'Quote To (Customer)', partyPlaceholder: 'Search customer...',
    lineLabel: 'Items / Services',
    showPayment: false, showPaidAmount: false,
    showTerms: true, showShippingAddr: false,
    extraFields: ['validUntil'],
  },
  proforma: {
    title: 'Proforma Invoice',
    statusOptions: ['draft','pending','approved','paid','cancelled'],
    showDueDate: true, showDeliveryDate: false, showShipping: true,
    showTransport: false, showCategory: false, showProject: true,
    partyLabel: 'Bill To (Customer)', partyPlaceholder: 'Search customer...',
    lineLabel: 'Products / Services',
    showPayment: true, showPaidAmount: true,
    showTerms: true, showShippingAddr: true,
    extraFields: [],
  },
  expense: {
    title: 'Expense',
    statusOptions: ['draft','pending','approved','paid','cancelled'],
    showDueDate: false, showDeliveryDate: false, showShipping: false,
    showTransport: false, showCategory: true, showProject: true,
    partyLabel: 'Paid To (Vendor)', partyPlaceholder: 'Search vendor...',
    lineLabel: 'Expense Items',
    showPayment: true, showPaidAmount: true,
    showTerms: false, showShippingAddr: false,
    extraFields: ['receiptNo'],
  },
  indirect_income: {
    title: 'Credit Note / Income',
    statusOptions: ['draft','pending','approved','paid','cancelled'],
    showDueDate: false, showDeliveryDate: false, showShipping: false,
    showTransport: false, showCategory: true, showProject: true,
    partyLabel: 'Received From', partyPlaceholder: 'Search contact...',
    lineLabel: 'Income Items',
    showPayment: true, showPaidAmount: true,
    showTerms: false, showShippingAddr: false,
    extraFields: [],
  },
  delivery_challan: {
    title: 'Delivery Challan',
    statusOptions: ['draft','pending','approved','dispatched','delivered','cancelled'],
    showDueDate: false, showDeliveryDate: true, showShipping: false,
    showTransport: true, showCategory: false, showProject: true,
    partyLabel: 'Deliver To', partyPlaceholder: 'Search customer...',
    lineLabel: 'Goods / Items',
    showPayment: false, showPaidAmount: false,
    showTerms: true, showShippingAddr: true,
    extraFields: ['vehicleNo','transportName'],
  },
  debit_note: {
    title: 'Debit Note',
    statusOptions: ['draft','pending','approved','cancelled'],
    showDueDate: true, showDeliveryDate: false, showShipping: false,
    showTransport: false, showCategory: false, showProject: false,
    partyLabel: 'Vendor (Debit To)', partyPlaceholder: 'Search vendor...',
    lineLabel: 'Return Items',
    showPayment: false, showPaidAmount: false,
    showTerms: true, showShippingAddr: false,
    extraFields: ['originalInvoiceNo'],
  },
};

// ── Shared small components ────────────────────────────────────
const inp = 'w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white';
const Lbl = ({ children, required }) => (
  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
    {children}{required && <span className="text-red-500 ml-0.5">*</span>}
  </label>
);

function Field({ label, required, children }) {
  return <div><Lbl required={required}>{label}</Lbl>{children}</div>;
}

// ── Image Upload Section ───────────────────────────────────────
function ImageUploadSection({ images, setImages }) {
  const inputRef = useRef();

  const handleFiles = (files) => {
    const newImgs = Array.from(files).map(f => ({
      id: Date.now() + Math.random(),
      name: f.name,
      url: URL.createObjectURL(f),
      file: f,
    }));
    setImages(prev => [...prev, ...newImgs]);
  };

  const onDrop = (e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  const remove = (id) => setImages(prev => prev.filter(img => img.id !== id));

  return (
    <div className="mt-4">
      <Lbl>Attach Images / Photos</Lbl>
      <div
        onDrop={onDrop}
        onDragOver={e => e.preventDefault()}
        onClick={() => inputRef.current?.click()}
        className="border-2 border-dashed border-gray-200 hover:border-indigo-400 rounded-xl p-4 text-center cursor-pointer transition-colors hover:bg-indigo-50/30 mb-3">
        <input ref={inputRef} type="file" multiple accept="image/*" className="hidden"
          onChange={e => { handleFiles(e.target.files); e.target.value = ''; }} />
        <CloudArrowUpIcon className="w-7 h-7 mx-auto text-gray-300 mb-1.5" />
        <p className="text-xs font-medium text-gray-500">Click or drag images here</p>
        <p className="text-[10px] text-gray-400 mt-0.5">JPG, PNG, WEBP — product photos, site images, receipts</p>
      </div>
      {images.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {images.map(img => (
            <div key={img.id} className="relative group rounded-xl overflow-hidden border border-gray-200 bg-gray-50 aspect-square">
              <img src={img.url} alt={img.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                <button type="button" onClick={(e) => { e.stopPropagation(); remove(img.id); }}
                  className="opacity-0 group-hover:opacity-100 p-1 bg-red-500 text-white rounded-full transition-opacity">
                  <XMarkIcon className="w-3.5 h-3.5" />
                </button>
              </div>
              <p className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[9px] px-1.5 py-0.5 truncate">{img.name}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────
export function AccountingForm({ isOpen, onClose, onSave, editing, defaultType = 'sale' }) {
  const cfg = TYPE_CONFIG[defaultType] || TYPE_CONFIG.sale;
  const [contacts, setContacts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [saving,   setSaving]   = useState(false);
  const [form,     setForm]     = useState(buildEmpty(defaultType));
  const [images,   setImages]   = useState([]);

  const [customFieldTemplates, setCustomFieldTemplates] = useState([]);
const [showFieldsManager, setShowFieldsManager] = useState(false);

const loadCustomFields = () => {
  customFieldsAPI.getAll({ appliesTo: form.type || 'sale' })
    .then(r => {
      const templates = r.data || [];
      setCustomFieldTemplates(templates);

      // Auto-populate form.customFields with any template not already added
      setForm(f => {
        const existingLabels = (f.customFields || []).map(cf => cf.label);
        const newOnes = templates
          .filter(t => !existingLabels.includes(t.label))
          .map(t => ({ label: t.label, type: t.type, position: t.position, value: '' }));
        return { ...f, customFields: [...(f.customFields || []), ...newOnes] };
      });
    })
    .catch(() => {});
};

const updateCustomField = (idx, value) => {
  setForm(f => {
    const updated = [...(f.customFields || [])];
    updated[idx] = { ...updated[idx], value };
    return { ...f, customFields: updated };
  });
};

  function buildEmpty(type) {
    return {
      type, status: 'draft',
      date:    format(new Date(), 'yyyy-MM-dd'),
      dueDate: '', deliveryDate: '', validUntil: '',
      contact: '', contactName: '', shippingAddress: '',
      paymentMode: '', paidAmount: 0, notes: '',
      category: '', project: '', poNumber: '', receiptNo: '',
      vehicleNo: '', transportName: '', originalInvoiceNo: '',
      lineItems: [{ ...EMPTY_LINE }],
      discount: 0, discountType: 'fixed',
      customFields: [],
    };
  }

  useEffect(() => {
    if (!isOpen) return;
    contactsAPI.getAll({ limit: 200 }).then(r => setContacts(r.data || [])).catch(() => {});
    projectsAPI.getAll({ limit: 100  }).then(r => setProjects(r.data || [])).catch(() => {});
  }, [isOpen]);

  useEffect(() => {
    if (editing) {
      setForm({
        type: editing.type || defaultType,
        status: editing.status || 'draft',
        date:    format(new Date(editing.date), 'yyyy-MM-dd'),
        dueDate: editing.dueDate     ? format(new Date(editing.dueDate),     'yyyy-MM-dd') : '',
        deliveryDate: editing.deliveryDate ? format(new Date(editing.deliveryDate), 'yyyy-MM-dd') : '',
        validUntil: editing.validUntil ? format(new Date(editing.validUntil), 'yyyy-MM-dd') : '',
        contact: editing.contact?._id || '',
        contactName: editing.contactName || '',
        shippingAddress: editing.shippingAddress || '',
        paymentMode: editing.paymentMode || '',
        paidAmount: editing.paidAmount || 0,
        notes: editing.notes || '',
        category: editing.category || '',
        project: editing.project?._id || '',
        poNumber: editing.poNumber || '',
        receiptNo: editing.receiptNo || '',
        vehicleNo: editing.vehicleNo || '',
        transportName: editing.transportName || '',
        originalInvoiceNo: editing.originalInvoiceNo || '',
        lineItems: editing.lineItems?.length ? editing.lineItems : [{ ...EMPTY_LINE }],
        discount: editing.discount || 0,
        discountType: editing.discountType || 'fixed',
        customFields: editing.customFields || [],
      });
    } else {
      setForm(buildEmpty(defaultType));
    }
  }, [editing, defaultType, isOpen]);

  useEffect(() => { if (isOpen) loadCustomFields(); }, [isOpen, form.type]);

  const s = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const calcLine = l => {
    const sub = Number(l.quantity) * Number(l.unitPrice);
    const tax = sub * (Number(l.taxRate) / 100);
    return { ...l, taxAmount: +tax.toFixed(2), total: +(sub + tax).toFixed(2) };
  };
  const updLine = (i, k, v) => setForm(f => { const ls = [...f.lineItems]; ls[i] = calcLine({ ...ls[i], [k]: v }); return { ...f, lineItems: ls }; });
  const addLine = ()  => setForm(f => ({ ...f, lineItems: [...f.lineItems, { ...EMPTY_LINE }] }));
  const delLine = i   => setForm(f => ({ ...f, lineItems: f.lineItems.filter((_, j) => j !== i) }));

  const totals = () => {
    const sub  = form.lineItems.reduce((a, l) => a + Number(l.quantity) * Number(l.unitPrice), 0);
    const tax  = form.lineItems.reduce((a, l) => a + Number(l.taxAmount), 0);
    const disc = form.discountType === 'percentage' ? sub * (form.discount / 100) : Number(form.discount);
    return { sub, tax, disc, total: sub + tax - disc };
  };

  const submit = async e => {
    e.preventDefault();
    const validLines = form.lineItems.filter(l => l.description && l.description.trim());
    if (validLines.length === 0) { alert('Please add at least one line item with a description.'); return; }
    setSaving(true);
    try {
      await onSave({
        ...form,
        contact:      form.contact      || null,
        dueDate:      form.dueDate      || null,
        deliveryDate: form.deliveryDate || null,
        validUntil:   form.validUntil   || null,
        paymentMode:  form.paymentMode  || null,
        project:      form.project      || null,
        lineItems:    validLines,
      });
    } finally { setSaving(false); }
  };

  const T = totals();

  // ── Color accent per type ──
  const ACCENT = {
    sale: 'indigo', purchase: 'blue', quotation: 'amber', proforma: 'violet',
    expense: 'red', indirect_income: 'green', delivery_challan: 'teal', debit_note: 'orange',
  };
  const accent = ACCENT[defaultType] || 'indigo';
  const headerBg = {
    sale: 'bg-indigo-600', purchase: 'bg-blue-600', quotation: 'bg-amber-500',
    proforma: 'bg-violet-600', expense: 'bg-red-600', indirect_income: 'bg-green-600',
    delivery_challan: 'bg-teal-600', debit_note: 'bg-orange-600',
  }[defaultType] || 'bg-indigo-600';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="" size="xl">
      <form onSubmit={submit} className="space-y-0 -mt-2">

        {/* ── Colored header band ── */}
        <div className={`${headerBg} rounded-2xl px-5 py-4 mb-5 flex items-center justify-between`}>
          <div>
            <h2 className="text-white font-bold text-lg">{editing ? 'Edit' : 'New'} {cfg.title}</h2>
            <p className="text-white/60 text-xs mt-0.5">Fill in the details below</p>
          </div>
          <div className="bg-white/20 rounded-xl px-3 py-1.5 text-white text-xs font-mono font-semibold">
            {editing?.referenceNo || 'Auto-generated'}
          </div>
        </div>

        {/* ── Section 1: Date + Status ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="Date" required>
            <input type="date" value={form.date} onChange={e => s('date', e.target.value)} className={inp} required />
          </Field>
          {cfg.showDueDate && (
            <Field label="Due Date">
              <input type="date" value={form.dueDate} onChange={e => s('dueDate', e.target.value)} className={inp} />
            </Field>
          )}
          {cfg.showDeliveryDate && (
            <Field label="Delivery Date">
              <input type="date" value={form.deliveryDate} onChange={e => s('deliveryDate', e.target.value)} className={inp} />
            </Field>
          )}
          {defaultType === 'quotation' && (
            <Field label="Valid Until">
              <input type="date" value={form.validUntil} onChange={e => s('validUntil', e.target.value)} className={inp} />
            </Field>
          )}
          <Field label="Status">
            <select value={form.status} onChange={e => s('status', e.target.value)} className={inp}>
              {cfg.statusOptions.map(v => <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>)}
            </select>
          </Field>
        </div>

        {/* ── Section 2: Party + Extra fields ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
          <Field label={cfg.partyLabel}>
            <select value={form.contact} onChange={e => s('contact', e.target.value)} className={inp}>
              <option value="">{cfg.partyPlaceholder}</option>
              {contacts.map(c => <option key={c._id} value={c._id}>{c.name}{c.company ? ' — ' + c.company : ''}</option>)}
            </select>
          </Field>
          <Field label="Or type name manually">
            <input value={form.contactName} onChange={e => s('contactName', e.target.value)} placeholder="Walk-in / manual entry" className={inp} />
          </Field>
        </div>

        {/* ── Section 3: Type-specific extra fields ── */}
        {(cfg.showCategory || cfg.showProject || cfg.extraFields?.length > 0 || cfg.showShippingAddr || cfg.showTransport) && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
            {cfg.showCategory && (
              <Field label="Category" required>
                <input value={form.category} onChange={e => s('category', e.target.value)} className={inp}
                  placeholder={defaultType === 'expense' ? 'Rent, Salary, Fuel...' : 'Commission, Interest...'} required />
              </Field>
            )}
            {cfg.showProject && (
              <Field label="Link to Project">
                <select value={form.project} onChange={e => s('project', e.target.value)} className={inp}>
                  <option value="">No project</option>
                  {projects.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                </select>
              </Field>
            )}
            {cfg.extraFields?.includes('poNumber') && (
              <Field label="PO / Reference No.">
                <input value={form.poNumber} onChange={e => s('poNumber', e.target.value)} placeholder="PO-2026-001" className={inp} />
              </Field>
            )}
            {cfg.extraFields?.includes('receiptNo') && (
              <Field label="Receipt / Bill No.">
                <input value={form.receiptNo} onChange={e => s('receiptNo', e.target.value)} placeholder="Bill number" className={inp} />
              </Field>
            )}
            {cfg.extraFields?.includes('vehicleNo') && (
              <Field label="Vehicle No.">
                <input value={form.vehicleNo} onChange={e => s('vehicleNo', e.target.value)} placeholder="GJ06 AB 1234" className={inp} />
              </Field>
            )}
            {cfg.extraFields?.includes('transportName') && (
              <Field label="Transport / Courier">
                <input value={form.transportName} onChange={e => s('transportName', e.target.value)} placeholder="Transporter name" className={inp} />
              </Field>
            )}
            {cfg.extraFields?.includes('originalInvoiceNo') && (
              <Field label="Original Invoice No.">
                <input value={form.originalInvoiceNo} onChange={e => s('originalInvoiceNo', e.target.value)} placeholder="Against invoice #" className={inp} />
              </Field>
            )}
            {cfg.showShippingAddr && (
              <Field label="Shipping Address">
                <input value={form.shippingAddress} onChange={e => s('shippingAddress', e.target.value)} placeholder="Ship to address" className={inp} />
              </Field>
            )}
          </div>
        )}

        {/* ── Custom Fields (Header position) ── */}
        {form.customFields?.filter(cf => cf.position === 'header').length > 0 && (
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Custom Fields</p>
              <button
                type="button"
                onClick={() => setShowFieldsManager(true)}
                className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium"
              >
                <Cog6ToothIcon className="w-3.5 h-3.5" /> Manage Fields
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {form.customFields.map((cf, idx) => cf.position === 'header' && (
                <div key={cf.label}>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                    {cf.label}
                  </label>
                  <input
                    type={cf.type === 'date' ? 'date' : cf.type === 'number' ? 'number' : 'text'}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={cf.value}
                    onChange={e => updateCustomField(form.customFields.indexOf(cf), e.target.value)}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Always show "Add Custom Field" trigger even if none exist yet */}
        {(!form.customFields || form.customFields.filter(cf => cf.position === 'header').length === 0) && (
          <button
            type="button"
            onClick={() => setShowFieldsManager(true)}
            className="flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-700 font-medium"
          >
            <PlusIcon className="w-3.5 h-3.5" /> Add Custom Field (PO Number, Reference, etc.)
          </button>
        )}

        {/* ── Section 4: Line Items ── */}
        <div className="mt-5">
          <div className="flex justify-between items-center mb-2">
            <Lbl>{cfg.lineLabel}</Lbl>
            <button type="button" onClick={addLine}
              className="flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-700 bg-indigo-50 hover:bg-indigo-100 px-2.5 py-1.5 rounded-lg transition-colors">
              <PlusIcon className="w-3.5 h-3.5" /> Add Line
            </button>
          </div>

          <div className="border border-gray-200 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-max">
                <thead className="bg-gray-900 text-white">
                  <tr>
                    <th className="text-left px-3 py-3 font-semibold w-10">#</th>
                    <th className="text-left px-3 py-3 font-semibold">Description</th>
                    <th className="text-right px-3 py-3 font-semibold w-20">HSN/SAC</th>
                    <th className="text-right px-3 py-3 font-semibold w-16">Qty</th>
                    <th className="text-right px-3 py-3 font-semibold w-16">Unit</th>
                    <th className="text-right px-3 py-3 font-semibold w-28">Price (₹)</th>
                    <th className="text-right px-3 py-3 font-semibold w-16">Tax %</th>
                    <th className="text-right px-3 py-3 font-semibold w-28">Total (₹)</th>
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {form.lineItems.map((l, i) => (
                    <tr key={i} className="hover:bg-indigo-50/30 transition-colors">
                      <td className="px-3 py-2.5 text-gray-400 text-center">{i + 1}</td>
                      <td className="px-3 py-2.5">
                        <input value={l.description} onChange={e => updLine(i, 'description', e.target.value)}
                          className="w-full bg-transparent border-0 focus:outline-none text-gray-800 text-sm" placeholder="Item / service description..." />
                      </td>
                      <td className="px-3 py-2.5">
                        <input value={l.hsn || ''} onChange={e => updLine(i, 'hsn', e.target.value)}
                          className="w-full bg-transparent border-0 focus:outline-none text-right text-gray-600" placeholder="0000" />
                      </td>
                      <td className="px-3 py-2.5">
                        <input type="number" value={l.quantity} onChange={e => updLine(i, 'quantity', e.target.value)}
                          className="w-full bg-transparent border-0 focus:outline-none text-right text-gray-800" min="0" step="0.01" />
                      </td>
                      <td className="px-3 py-2.5">
                        <select value={l.unit || 'pcs'} onChange={e => updLine(i, 'unit', e.target.value)}
                          className="w-full bg-transparent border-0 focus:outline-none text-gray-600 text-xs">
                          {['pcs','kg','ltr','mtr','box','set','nos','sqft','day','hr'].map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2.5">
                        <input type="number" value={l.unitPrice} onChange={e => updLine(i, 'unitPrice', e.target.value)}
                          className="w-full bg-transparent border-0 focus:outline-none text-right text-gray-800" min="0" step="0.01" />
                      </td>
                      <td className="px-3 py-2.5">
                        <select value={l.taxRate} onChange={e => updLine(i, 'taxRate', e.target.value)}
                          className="w-full bg-transparent border-0 focus:outline-none text-right text-gray-800">
                          {[0, 5, 12, 18, 28].map(r => <option key={r} value={r}>{r}%</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2.5 text-right font-bold text-gray-900">
                        {(l.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-2 py-2.5">
                        {form.lineItems.length > 1 && (
                          <button type="button" onClick={() => delLine(i)} className="text-gray-300 hover:text-red-500 transition-colors">
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="border-t border-gray-200 bg-gray-50 px-5 py-4 flex justify-end">
              <div className="w-80 space-y-2 text-sm">
                <div className="flex justify-between text-gray-500">
                  <span>Subtotal</span><span>₹{T.sub.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-gray-500">
                  <span>Tax (GST)</span><span>₹{T.tax.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex items-center justify-between text-gray-500 gap-2">
                  <span>Discount</span>
                  <div className="flex items-center gap-1.5">
                    <select value={form.discountType} onChange={e => s('discountType', e.target.value)}
                      className="text-xs border border-gray-200 rounded-lg px-1.5 py-1 bg-white focus:outline-none">
                      <option value="fixed">₹ Fixed</option>
                      <option value="percentage">% Percent</option>
                    </select>
                    <input type="number" value={form.discount} onChange={e => s('discount', Number(e.target.value))}
                      className="w-24 text-right border border-gray-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500" min="0" />
                  </div>
                </div>
                {T.disc > 0 && (
                  <div className="flex justify-between text-green-600 text-xs">
                    <span>Discount Applied</span><span>−₹{T.disc.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-base text-gray-900 border-t border-gray-300 pt-2">
                  <span>Grand Total</span>
                  <span className="text-indigo-600">₹{T.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Section 5: Payment ── */}
        {cfg.showPayment && (
          <div className="grid grid-cols-2 gap-3 mt-4">
            <Field label="Payment Mode">
              <select value={form.paymentMode} onChange={e => s('paymentMode', e.target.value)} className={inp}>
                <option value="">Select mode...</option>
                {PAYMENT_MODES.map(v => (
                  <option key={v} value={v}>{v.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                ))}
              </select>
            </Field>
            {cfg.showPaidAmount && (
              <Field label="Amount Paid (₹)">
                <input type="number" value={form.paidAmount} onChange={e => s('paidAmount', Number(e.target.value))}
                  min="0" placeholder="0" className={inp} />
              </Field>
            )}
          </div>
        )}

        {/* ── Section 6: Notes / Terms ── */}
        <div className="mt-4">
          <Field label={cfg.showTerms ? 'Notes / Terms & Conditions' : 'Notes / Remarks'}>
            <textarea value={form.notes} onChange={e => s('notes', e.target.value)} rows={2}
              placeholder={cfg.showTerms ? 'Payment terms, delivery conditions, warranty...' : 'Additional remarks...'}
              className={inp + ' resize-none'} />
          </Field>
        </div>

        {form.customFields?.filter(cf => cf.position === 'footer').length > 0 && (
  <div className="grid grid-cols-2 gap-3">
    {form.customFields.map((cf, idx) => cf.position === 'footer' && (
      <div key={cf.label}>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
          {cf.label}
        </label>
        <input
          type={cf.type === 'date' ? 'date' : cf.type === 'number' ? 'number' : 'text'}
          className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          value={cf.value}
          onChange={e => updateCustomField(form.customFields.indexOf(cf), e.target.value)}
        />
      </div>
    ))}
  </div>
)}

        {/* ── Section 7: Image Attachments ── */}
        <ImageUploadSection images={images} setImages={setImages} />

        {/* ── Footer buttons ── */}
        <div className="flex gap-3 pt-4 border-t border-gray-100 mt-5">
          <button type="submit" disabled={saving}
            className={`flex-1 py-3 rounded-xl font-semibold text-white text-sm ${headerBg} hover:opacity-90 disabled:opacity-50 transition-opacity`}>
            {saving ? 'Saving...' : `${editing ? 'Update' : 'Create'} ${cfg.title}`}
          </button>
          <button type="button" onClick={onClose}
            className="px-6 py-3 rounded-xl font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 text-sm transition-colors">
            Cancel
          </button>
        </div>

      </form>

      <CustomFieldsManager
        isOpen={showFieldsManager}
        onClose={() => setShowFieldsManager(false)}
        appliesTo={form.type || 'sale'}
        onFieldsChanged={loadCustomFields}
      />

    </Modal>
  );
}